import React, { ReactNode, useCallback, useRef, useState } from 'react';

const STORAGE_KEY = 'megaphony.editor.previewHeight';
const DEFAULT_HEIGHT = 380;
const SNAP_CLOSED_BELOW = 120; // dragging under this collapses the preview
const MIN_DRAG = 0;

function loadHeight(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_HEIGHT;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return DEFAULT_HEIGHT;
    return n; // 0 = collapsed
  } catch {
    return DEFAULT_HEIGHT;
  }
}

function storeHeight(h: number): void {
  try { window.localStorage.setItem(STORAGE_KEY, String(Math.round(h))); } catch { /* ignore */ }
}

interface ResizablePreviewProps {
  children: ReactNode;
}

/**
 * Height-resizable shell for the editor preview. Drag the handle under
 * the preview to grow/shrink it; the height persists in localStorage
 * across page loads. Dragging below the snap threshold collapses the
 * preview entirely (just the timeline workflow) — a "Show preview"
 * button or dragging the handle back up restores it. Purely a viewing
 * convenience: aspect ratio and render output are untouched, and the
 * canvas stays mounted while collapsed so playback elements survive.
 */
export default function ResizablePreview({ children }: ResizablePreviewProps) {
  const [height, setHeight] = useState<number>(loadHeight);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);

  const collapsed = height < SNAP_CLOSED_BELOW;

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: height };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [height]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const maxH = Math.round(window.innerHeight * 0.75);
    const next = Math.min(maxH, Math.max(MIN_DRAG, drag.startH + (e.clientY - drag.startY)));
    setHeight(next);
  }, []);

  const onPointerUp = useCallback(() => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setHeight((h) => {
      const settled = h < SNAP_CLOSED_BELOW ? 0 : h;
      storeHeight(settled);
      return settled;
    });
  }, []);

  const expand = useCallback(() => {
    setHeight(DEFAULT_HEIGHT);
    storeHeight(DEFAULT_HEIGHT);
  }, []);

  return (
    <div className="preview-resize">
      <div
        className={collapsed ? 'preview-resize-body is-collapsed' : 'preview-resize-body'}
        style={{ height: collapsed ? 0 : height }}
        aria-hidden={collapsed}
      >
        {children}
      </div>
      {collapsed ? (
        <button type="button" className="ghost btn--sm preview-resize-show" onClick={expand}>
          ▾ Show preview
        </button>
      ) : (
        <div
          className="preview-resize-handle"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize preview (drag down past minimum to hide)"
          title="Drag to resize the preview; drag all the way up to hide it"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="preview-resize-grip" aria-hidden />
        </div>
      )}
    </div>
  );
}
